import { useState } from 'react';
import { Link } from 'react-router-dom';
import { StoreCatalogItem } from '../../models';
import { formatCurrency } from '../../utils/currency';
import { safeImageUrl } from '../../utils/url';

interface ProductCardProps {
  product: StoreCatalogItem;
  colorId?: number;
  variationValue?: string;
}

export function ProductCard({ product, colorId, variationValue }: ProductCardProps) {
  const displayPrice = product.promotionalPrice ?? product.price;
  const hasPromotion = !!product.promotionalPrice;

  const images = product.imageUrls?.length ? product.imageUrls : product.mainImageUrl ? [product.mainImageUrl] : [];
  const [currentIndex, setCurrentIndex] = useState(0);
  const hasMultipleImages = images.length > 1;

  const linkParams = new URLSearchParams();
  if (colorId !== undefined) linkParams.set('color', String(colorId));
  if (variationValue !== undefined) linkParams.set('var', variationValue);
  const linkTo = `/produtos/${product.id}${linkParams.size > 0 ? `?${linkParams.toString()}` : ''}`;

  const handlePrev = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const handleNext = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  return (
    <Link
      to={linkTo}
      className="group bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-200 flex flex-col"
    >
      <div className="relative aspect-square bg-champagne-light overflow-hidden">
        {images.length > 0 ? (
          <img
            src={safeImageUrl(images[currentIndex])}
            alt={product.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-text-light text-4xl">
            👗
          </div>
        )}

        {hasMultipleImages && (
          <>
            <button
              onClick={handlePrev}
              aria-label="Imagem anterior"
              className="absolute left-1.5 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/90 hover:bg-white text-text-dark flex items-center justify-center opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity duration-200 shadow-sm"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              onClick={handleNext}
              aria-label="Próxima imagem"
              className="absolute right-1.5 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/90 hover:bg-white text-text-dark flex items-center justify-center opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity duration-200 shadow-sm"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
              {images.map((_, idx) => (
                <span
                  key={idx}
                  className={`w-1.5 h-1.5 rounded-full transition-colors ${
                    idx === currentIndex ? 'bg-chocolate' : 'bg-white/70'
                  }`}
                />
              ))}
            </div>
          </>
        )}

        {!product.hasStock && (
          <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
            <span className="bg-white text-text-dark text-xs font-semibold px-2 py-1 rounded">
              Sem estoque
            </span>
          </div>
        )}
      </div>

      <div className="p-3 flex flex-col gap-1 flex-1">
        <p className="text-xs text-text-light">{product.categoryName}</p>
        <h3 className="font-medium text-text-dark text-sm leading-snug line-clamp-2">{product.name}</h3>
        <div className="mt-auto pt-2 flex items-baseline gap-2">
          {hasPromotion && (
            <span className="text-text-light text-xs line-through">
              R$ {formatCurrency(product.price)}
            </span>
          )}
          <span className={`font-bold ${hasPromotion ? 'text-chocolate' : 'text-text-dark'}`}>
            R$ {formatCurrency(displayPrice)}
          </span>
        </div>
      </div>
    </Link>
  );
}
